import { SelectedPaymentOption } from '@/types/interfaces/booking_options'
import { GetPaymentResponse, VisitShowResponse } from '@/types/interfaces/visit'
import { isWithin24Hours, shouldShowFeePolicy } from '@/utils/cancellationFeeUtils'

export type CheckoutInsuranceStatus = 'in_network' | 'out_of_network' | 'no_insurance'

export interface CheckoutAnalyticsBasePayload {
    visit_id: string
    visit_type: string
    sku_id: string
    billing_type: SelectedPaymentOption
    insurance_status: CheckoutInsuranceStatus
}

export interface CheckoutAnalyticsDetailPayload {
    price_guaranteed: boolean
    price_fallback: boolean
    price_cents: number
    cancellation_fee_shown: boolean
    cancellation_fee_cents?: number
    within_24_hours: boolean
    timeslot?: string
    utah_financial_agreement_required: boolean
    bridge_agreements_required: boolean
    mdsave_agreement_required: boolean
}

export function checkoutInsuranceStatus(
    hasMemberId: boolean,
    inNetworkStatus: string | undefined,
): CheckoutInsuranceStatus {
    if (!hasMemberId) {
        return 'no_insurance'
    }
    if (inNetworkStatus === 'true') {
        return 'in_network'
    }
    if (inNetworkStatus === 'false') {
        return 'out_of_network'
    }
    return 'no_insurance'
}

export function buildCheckoutAnalyticsPayload(
    visitId: string,
    visitInfo: GetPaymentResponse,
): CheckoutAnalyticsBasePayload {
    return {
        visit_id: visitId,
        visit_type: visitInfo.visit_type,
        sku_id: visitInfo.sku_id,
        billing_type: visitInfo.payment_method,
        insurance_status: checkoutInsuranceStatus(
            !!visitInfo.insurance?.has_member_id,
            visitInfo.insurance?.in_network_status,
        ),
    }
}

export function buildCheckoutAnalyticsDetailPayload(
    visitInfo: GetPaymentResponse,
    overrides?: Partial<CheckoutAnalyticsDetailPayload>,
): CheckoutAnalyticsDetailPayload {
    const cancellationFeeShown = shouldShowFeePolicy(visitInfo)
    const payload: CheckoutAnalyticsDetailPayload = {
        price_guaranteed: visitInfo.price_guaranteed ?? false,
        price_fallback: visitInfo.price_fallback ?? false,
        price_cents: Number(visitInfo.price),
        cancellation_fee_shown: cancellationFeeShown,
        within_24_hours: isWithin24Hours(visitInfo.scheduled_time),
        utah_financial_agreement_required: !!visitInfo.policy_agreement_requirements?.utah_requirement,
        bridge_agreements_required: !!visitInfo.policy_agreement_requirements?.bridge,
        mdsave_agreement_required: !!visitInfo.policy_agreement_requirements?.md_save,
        ...overrides,
    }

    if (cancellationFeeShown && visitInfo.cancellation_fee_amount_cents != null) {
        payload.cancellation_fee_cents = visitInfo.cancellation_fee_amount_cents
    }

    if (visitInfo.scheduled_time) {
        payload.timeslot = visitInfo.scheduled_time
    }

    return payload
}

export function buildBookedAnalyticsPayload(visitId: string, visit: VisitShowResponse) {
    return {
        visit_id: visitId,
        visit_type: visit.visit_type,
        sku_id: visit.sku_id,
        billing_type: visit.payment_method,
        insurance_status: checkoutInsuranceStatus(
            !!visit.billing_details?.insurance_member_id,
            visit.billing_details?.insurance_in_network,
        ),
        within_24_hours: isWithin24Hours(visit.scheduled_time),
        timeslot: visit.scheduled_time,
        price_cents: Number(visit.billing_details?.price),
        follow_up_visit: !!visit.follow_up_visit,
        abn_signature_required: visit.abn_signature_required,
        duration_minutes: visit.duration_minutes,
    }
}
